'use client'

import { Accordion } from '@fukku/accordion/Accordion'
import type { AccordionData } from '@fukku/accordion/Accordion.types'
import textBodyM from '@fukku/styles/texts/textBodyM.module.css'
import textHeadingM from '@fukku/styles/texts/textHeadingM.module.css'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useEffect, useRef, useState } from 'react'
import { cx } from 'utils/cx'
import { useAnalytics } from '../../contexts/AnalyticsProvider/AnalyticsProvider'
import type { Color } from '../../types/FullProduct'
import {
	getQualities,
	type QualityData,
} from '../../utils/getQualities/getQualities'
import { getQualityPercentageRange } from '../../utils/getQualityPercentageRange/getQualityPercentageRange'
import styles from './Qualities.module.css'

interface QualitiesProps {
	readonly color: Color
}

export const Qualities = ({
	color: { textileDetailComposition },
}: QualitiesProps) => {
	const { t } = useLabels()
	const { sendViewProductQualitiesEvent } = useAnalytics()
	const ref = useRef<HTMLDivElement | null>(null)
	const entry = useIntersectionObserver(ref, {
		threshold: 0,
	})
	const [sentExpandedElementEvent, setSentExpandedElementEvent] =
		useState(false)

	const qualities: QualityData[] = getQualities(textileDetailComposition)

	useEffect(() => {
		if (
			!sentExpandedElementEvent &&
			entry?.isIntersecting &&
			qualities.length > 0
		) {
			sendViewProductQualitiesEvent({
				qualityId: qualities[0].id,
				qualityPosition: 1,
			})
			setSentExpandedElementEvent(true)
		}
	}, [entry])

	const sendAnalyticsEvent = (qualityPosition: number, qualityId: string) => {
		sendViewProductQualitiesEvent({
			qualityId,
			qualityPosition,
		})
	}

	if (!qualities) {
		return null
	}

	const materials: AccordionData[] = qualities.map(
		(quality: QualityData, index: number) => ({
			title: t(quality.title),
			subtitle:
				quality.percent < 100
					? replacePlaceholders(t('pdp.materials.percentage.text'), {
							1: getQualityPercentageRange(quality.percent),
						})
					: '100%',
			content: (
				<p className={cx(styles.qualityDescription, textBodyM.className)}>
					{t(quality.subtitle)}
				</p>
			),
			shouldScroll: false,
			onExpanded: () => sendAnalyticsEvent(index + 1, quality.id),
			showCloseIcon: qualities.length > 1,
			showExpanded: index === 0,
		})
	)

	if (materials.length === 0) {
		return null
	}

	return (
		<div className={styles.qualities} ref={ref}>
			<h2 className={textHeadingM.className}>
				{t('pdp.garmentQualities.title')}
			</h2>
			<Accordion id='pdp.qualities' data={materials} shouldCollapseOthers />
		</div>
	)
}
